/**
 * Utility functions for text processing and HTML entity decoding
 */

/**
 * Decode HTML entities in text
 * @param {string} text - Text containing HTML entities
 * @returns {string} - Decoded text
 */
export const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Create a temporary element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

/**
 * Comprehensive HTML entity decoder that handles both named and numeric entities
 * @param {string} text - Text containing HTML entities
 * @returns {string} - Decoded text
 */
export const decodeAllHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;

    // First decode using the DOM approach for most entities
    let decoded = decodeHtmlEntities(text);

    // Then handle specific numeric entities that might not be caught
    const entityMap = {
        // Superscript numbers (common in offers)
        '&#185;': '¹',   // Superscript 1
        '&#178;': '²',   // Superscript 2
        '&#179;': '³',   // Superscript 3
        '&#8308;': '⁴',  // Superscript 4
        '&#8309;': '⁵',  // Superscript 5
        '&#8310;': '⁶',  // Superscript 6
        '&#8311;': '⁷',  // Superscript 7
        '&#8312;': '⁸',  // Superscript 8
        '&#8313;': '⁹',  // Superscript 9
        '&#8304;': '⁰',  // Superscript 0

        // Line breaks and whitespace
        '&#10;': '\n',   // Line feed
        '&#13;': '\r',   // Carriage return
        '&#13;&#10;': '\n', // CRLF to LF
        '&#32;': ' ',    // Space

        // Common symbols
        '&#8482;': '™',  // Trademark symbol
        '&#9670;': '◆',  // Diamond symbol
        '&#8226;': '•',  // Bullet point
        '&#8217;': "'",  // Right single quotation mark
        '&#8220;': '"',  // Left double quotation mark
        '&#8221;': '"',  // Right double quotation mark
        '&#8230;': '…',  // Horizontal ellipsis
        '&#8212;': '—',  // Em dash
        '&#8211;': '–',  // En dash
        '&#8364;': '€',  // Euro sign
        '&#8249;': '‹',  // Single left-pointing angle quotation mark
        '&#8250;': '›',  // Single right-pointing angle quotation mark
        '&#8216;': "'",  // Left single quotation mark
        '&#8218;': '‚',  // Single low-9 quotation mark
        '&#8222;': '„',  // Double low-9 quotation mark
        '&#8594;': '→',  // Rightwards arrow
        '&#8592;': '←',  // Leftwards arrow
        '&#8593;': '↑',  // Upwards arrow
        '&#8595;': '↓',  // Downwards arrow
        '&#174;': '®',   // Registered trademark
        '&#169;': '©',   // Copyright symbol
        '&#8804;': '≤',  // Less-than or equal to
        '&#8805;': '≥',  // Greater-than or equal to
        '&#177;': '±',   // Plus-minus sign
        '&#215;': '×',   // Multiplication sign
        '&#247;': '÷',   // Division sign
        '&#8240;': '‰',  // Per mille sign
        '&#8734;': '∞',  // Infinity
        '&#8721;': '∑',  // N-ary summation
        '&#8706;': '∂',  // Partial differential
        '&#8719;': '∏',  // N-ary product
        '&#8730;': '√',  // Square root
        '&#8747;': '∫',  // Integral
        '&#8776;': '≈',  // Almost equal to
        '&#8800;': '≠',  // Not equal to
        '&#8801;': '≡',  // Identical to
        '&#8745;': '∩',  // Intersection
        '&#8746;': '∪',  // Union
        '&#8712;': '∈',  // Element of
        '&#8713;': '∉',  // Not an element of
        '&#8715;': '∋',  // Contains as member
        '&#8834;': '⊂',  // Subset of
        '&#8835;': '⊃',  // Superset of
        '&#8838;': '⊆',  // Subset of or equal to
        '&#8839;': '⊇'   // Superset of or equal to
    };

    // Replace specific numeric entities
    for (const [entity, symbol] of Object.entries(entityMap)) {
        decoded = decoded.replace(new RegExp(entity, 'g'), symbol);
    }

    return decoded;
};

/**
 * Format offer text by cleaning up whitespace and decoding entities
 * @param {string} text - Raw offer text
 * @returns {string} - Cleaned and formatted text
 */
export const formatOfferText = (text) => {
    if (!text || typeof text !== 'string') return '';

    // Decode HTML entities
    let formatted = decodeAllHtmlEntities(text);

    // Clean up excessive whitespace but preserve intentional breaks
    formatted = formatted
        .replace(/[ \t]+/g, ' ')  // Replace multiple spaces/tabs with single space
        .replace(/\n\s*\n\s*\n+/g, '\n\n')  // Replace 3+ newlines with double newline
        .replace(/\n\s*\n/g, '\n\n')  // Ensure double newlines are consistent
        .trim();

    return formatted;
};

/**
 * Format text with proper bullet spacing and line breaks
 * @param {string} text - Text that may contain bullets and lists
 * @returns {string} - Formatted text with proper spacing
 */
export const formatTextWithBullets = (text) => {
    if (!text || typeof text !== 'string') return '';

    let formatted = formatOfferText(text);

    // Handle different bullet types and add proper spacing
    formatted = formatted
        // Add line breaks before bullet points if they're not already on new lines
        .replace(/([.!?])\s*([•·●▪▫‣⁃*-])/g, '$1\n$2')
        .replace(/([a-z])\s*([•·●▪▫‣⁃])/g, '$1\n$2')
        // Add space after bullet points if missing
        .replace(/([•·●▪▫‣⁃*-])([A-Za-z])/g, '$1 $2')
        // Handle numbered lists
        .replace(/([.!?])\s*(\d+\.)/g, '$1\n$2')
        .replace(/([a-z])\s*(\d+\.)/g, '$1\n$2')
        // Add space after numbered bullets if missing
        .replace(/(\d+\.)([A-Za-z])/g, '$1 $2')
        // Handle parenthetical lists
        .replace(/([.!?])\s*(\([a-zA-Z0-9]+\))/g, '$1\n$2')
        .replace(/([a-z])\s*(\([a-zA-Z0-9]+\))/g, '$1\n$2')
        // Clean up any excessive spacing we may have created
        .replace(/\n\s*\n\s*\n+/g, '\n\n')
        .replace(/\n\s+/g, '\n')
        .trim();

    return formatted;
};

/**
 * Split text into paragraphs and bullet points for better rendering
 * @param {string} text - Text to split
 * @returns {Array} - Array of objects with type and content
 */
export const parseTextStructure = (text) => {
    if (!text || typeof text !== 'string') return [];

    const formatted = formatTextWithBullets(text);
    const lines = formatted.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    const structure = [];
    let currentParagraph = '';

    for (const line of lines) {
        // Check if line is a bullet point
        if (/^[•·●▪▫‣⁃*-]\s/.test(line) || /^\d+\.\s/.test(line) || /^\([a-zA-Z0-9]+\)\s/.test(line)) {
            // If we have accumulated paragraph text, add it first
            if (currentParagraph.trim()) {
                structure.push({
                    type: 'paragraph',
                    content: currentParagraph.trim()
                });
                currentParagraph = '';
            }

            // Add the bullet point
            structure.push({
                type: 'bullet',
                content: line
            });
        } else {
            // Accumulate paragraph text
            if (currentParagraph) {
                currentParagraph += ' ' + line;
            } else {
                currentParagraph = line;
            }
        }
    }

    // Add any remaining paragraph text
    if (currentParagraph.trim()) {
        structure.push({
            type: 'paragraph',
            content: currentParagraph.trim()
        });
    }

    return structure;
};

/**
 * Check if text appears to be fine print based on content patterns
 * @param {string} text - Text to check
 * @returns {boolean} - True if appears to be fine print
 */
export const isFinePrintText = (text) => {
    if (!text || typeof text !== 'string') return false;

    const finePrintKeywords = [
        'all payments based',
        'bank approval needed',
        'must qualify',
        'ask for details',
        'apr',
        'months',
        'replacement of',
        'additional',
        'select products only',
        'previous sales excluded',
        'cannot be combined',
        'fully licensed',
        'insured',
        'independently owned',
        'operated',
        'privacy policy',
        'terms',
        'conditions',
        'disclaimer',
        'footnote',
        'see details',
        'restrictions apply',
        'limited time',
        'while supplies last',
        'offer expires',
        'not valid with',
        'one per household',
        'residential only',
        'commercial properties',
        'minimum purchase',
        'installation required'
    ];

    const lowerText = text.toLowerCase();

    // Check for footnote markers at start
    const hasFootnoteMarker = /^[¹²³⁴⁵⁶⁷⁸⁹*†‡§]/.test(text) || /^\(\d+\)/.test(text) || /^\*/.test(text);

    // Check for fine print keywords
    const hasKeywords = finePrintKeywords.some(keyword => lowerText.includes(keyword));

    // Check for percentage patterns (often in fine print)
    const hasPercentagePattern = /\d+(\.\d+)?%/.test(text);

    // Check for month/term patterns
    const hasTermPattern = /\d+\s+months?/i.test(text) || /apr/i.test(text);

    return hasFootnoteMarker || hasKeywords || hasPercentagePattern || hasTermPattern;
};

/**
 * Split offer content into main content and fine print
 * @param {Object} offer - Offer object with title, details, terms, finePrint
 * @returns {Object} - Object with mainContent and finePrint arrays
 */
export const categorizeOfferContent = (offer) => {
    const mainContent = [];
    const finePrintContent = [];

    if (!offer) return { mainContent, finePrintContent };

    // Process title (always main content)
    if (offer.title) {
        mainContent.push({
            type: 'title',
            content: formatOfferText(offer.title),
            original: offer.title
        });
    }

    // Process details
    if (offer.details) {
        const formattedDetails = formatOfferText(offer.details);
        if (isFinePrintText(formattedDetails)) {
            finePrintContent.push({
                type: 'details',
                content: formattedDetails,
                original: offer.details
            });
        } else {
            mainContent.push({
                type: 'details',
                content: formattedDetails,
                original: offer.details
            });
        }
    }

    // Process terms
    if (offer.terms) {
        const formattedTerms = formatOfferText(offer.terms);
        if (isFinePrintText(formattedTerms)) {
            finePrintContent.push({
                type: 'terms',
                content: formattedTerms,
                original: offer.terms
            });
        } else {
            mainContent.push({
                type: 'terms',
                content: formattedTerms,
                original: offer.terms
            });
        }
    }

    // Process fine print (always fine print)
    if (offer.finePrint) {
        finePrintContent.push({
            type: 'finePrint',
            content: formatOfferText(offer.finePrint),
            original: offer.finePrint
        });
    }

    return { mainContent, finePrintContent };
};

/**
 * Get a clean, searchable version of text for comparison
 * @param {string} text - Text to clean
 * @returns {string} - Cleaned text for comparison
 */
export const getComparableText = (text) => {
    if (!text || typeof text !== 'string') return '';

    return formatOfferText(text)
        .toLowerCase()
        .replace(/[^\w\s]/g, ' ')  // Replace punctuation with spaces
        .replace(/\s+/g, ' ')      // Normalize whitespace
        .trim();
};
